import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import { sanitizePhoneInput, validateOptionalPhone } from '../../utils/validation';
import PasswordInput from '../../components/SuperAdmin/PasswordInput';
import { buildPageList } from '../../utils/buildPageList';
import { ExportMenu, DateRangeFilter, ResetFiltersButton } from '../../components/SuperAdmin/ListTools';
import { fetchAllPages, dateRangeParams, formatExportDate } from '../../utils/exportData';

const EMPTY_FILTERS = { search: '', role: '', status: '', from: '', to: '' };
const toApiParams = ({ from, to, ...rest }) => ({ ...rest, ...dateRangeParams(from, to) });

const AdminsList = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS, page: 1, limit: 20 });
  const setFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch, page: 1 }));
  const filtersActive = Object.keys(EMPTY_FILTERS).some((k) => filters[k]);
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'support',
    permissions: []
  });
  const [formError, setFormError] = useState('');

  const availablePermissions = [
    { id: 'manage_organizations', label: 'Manage Organizations' },
    { id: 'manage_plans', label: 'Manage Plans' },
    { id: 'manage_subscriptions', label: 'Manage Subscriptions' },
    { id: 'view_analytics', label: 'View Analytics' },
    { id: 'manage_users', label: 'Manage Users' },
    { id: 'view_reports', label: 'View Reports' },
    { id: 'manage_support', label: 'Manage Support' },
    { id: 'manage_channel_partners', label: 'Manage Channel Partners' },
    { id: 'manage_billing', label: 'Manage Billing' }
  ];

  const roleLabels = {
    super_admin: 'Super Admin',
    support: 'Support',
    billing: 'Billing'
  };

  const { data: adminsData, isLoading } = useQuery({
    queryKey: ['superadmin-admins', filters],
    queryFn: () => superAdminAPI.getAdmins(toApiParams(filters))
  });

  const admins = adminsData?.data?.data || [];
  const pagination = adminsData?.data?.pagination || {};

  const createMutation = useMutation({
    mutationFn: superAdminAPI.createAdmin,
    onSuccess: () => {
      queryClient.invalidateQueries(['superadmin-admins']);
      closeModal();
    },
    onError: (error) => setFormError(error.response?.data?.message || 'Failed to create admin')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => superAdminAPI.updateAdmin(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['superadmin-admins']);
      closeModal();
    },
    onError: (error) => setFormError(error.response?.data?.message || 'Failed to update admin')
  });

  const deleteMutation = useMutation({
    mutationFn: superAdminAPI.deleteAdmin,
    onSuccess: () => queryClient.invalidateQueries(['superadmin-admins'])
  });

  const closeModal = () => {
    setShowModal(false);
    setEditingAdmin(null);
    setFormError('');
    setFormData({
      name: '', email: '', password: '', phone: '', role: 'support', permissions: []
    });
  };

  const openEditModal = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      password: '',
      phone: admin.phone || '',
      role: admin.role,
      permissions: admin.permissions || []
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;

    if (name === 'permissions') {
      setFormData(prev => ({
        ...prev,
        permissions: checked
          ? [...prev.permissions, value]
          : prev.permissions.filter(p => p !== value)
      }));
    } else {
      const sanitizedValue = name === 'phone' ? sanitizePhoneInput(value) : value;
      setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name || !formData.email || (!editingAdmin && !formData.password)) {
      setFormError('Please fill in all required fields');
      return;
    }

    // Validate phone if provided
    if (formData.phone && formData.phone.trim()) {
      const phoneValidation = validateOptionalPhone(formData.phone);
      if (!phoneValidation.isValid) {
        setFormError(phoneValidation.error);
        return;
      }
    }

    const submitData = { ...formData };
    if (editingAdmin && !submitData.password) {
      delete submitData.password;
    }

    if (editingAdmin) {
      updateMutation.mutate({ id: editingAdmin._id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleDelete = (adminId, adminName) => {
    if (window.confirm(`Are you sure you want to delete "${adminName}"? This action cannot be undone.`)) {
      deleteMutation.mutate(adminId);
    }
  };

  const getStatusBadge = (status) => {
    return status === 'active' ? 'badge-success' : 'badge-danger';
  };

  return (
    <div className="admins-page">
      <div className="page-header">
        <div className="header-left">
          <h1>Admin Users</h1>
          <p>Manage platform administrators</p>
        </div>
        <div className="header-right">
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + Add Admin
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search name, email, phone..."
          value={filters.search}
          onChange={(e) => setFilter({ search: e.target.value })}
          className="search-input"
        />
        <select value={filters.role} onChange={(e) => setFilter({ role: e.target.value })} className="filter-select">
          <option value="">All Roles</option>
          {Object.entries(roleLabels).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={filters.status} onChange={(e) => setFilter({ status: e.target.value })} className="filter-select">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <DateRangeFilter
          label="Created"
          from={filters.from}
          to={filters.to}
          onChange={({ from, to }) => setFilter({ from, to })}
        />
        <ResetFiltersButton active={filtersActive} onReset={() => setFilter(EMPTY_FILTERS)} />
        <ExportMenu
          fileName="admin-users"
          getRows={() => fetchAllPages(superAdminAPI.getAdmins, toApiParams(filters))}
          columns={[
            { header: 'Name', value: (a) => a.name },
            { header: 'Email', value: (a) => a.email },
            { header: 'Phone', value: (a) => a.phone },
            { header: 'Role', value: (a) => roleLabels[a.role] || a.role },
            { header: 'Status', value: (a) => a.status },
            { header: 'Permissions', value: (a) => (a.permissions || []).map((p) => availablePermissions.find((x) => x.id === p)?.label || p) },
            { header: 'Last Login', value: (a) => formatExportDate(a.lastLoginAt) },
            { header: 'Created', value: (a) => formatExportDate(a.createdAt) },
          ]}
        />
      </div>

      <div className="table-container">
        {isLoading ? (
          <div className="loading">Loading admins...</div>
        ) : admins.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">👥</div>
            <h3>No admin users found</h3>
            <p>Add your first platform administrator</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => (
                  <tr key={admin._id}>
                    <td>
                      <div className="admin-name">
                        <span className="name">{admin.name}</span>
                      </div>
                    </td>
                    <td>{admin.email}</td>
                    <td>
                      <span className="badge badge-info">
                        {roleLabels[admin.role] || admin.role}
                      </span>
                    </td>
                    <td>{admin.phone || '-'}</td>
                    <td>
                      <span className={`badge ${getStatusBadge(admin.status)}`}>
                        {admin.status}
                      </span>
                    </td>
                    <td>{new Date(admin.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-sm btn-outline" onClick={() => openEditModal(admin)}>
                          Edit
                        </button>
                        {admin.role !== 'super_admin' && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(admin._id, admin.name)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination (server-side: page + limit sent to the API) */}
      {pagination.total > 0 && (
        <div className="pagination">
          <div className="pagination-info">
            <span className="page-info">
              Showing {(pagination.page - 1) * pagination.limit + 1}–
              {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
              of {pagination.total}
            </span>
            <div className="rows-per-page">
              <label htmlFor="admins-rows-per-page">Rows per page:</label>
              <select
                id="admins-rows-per-page"
                value={filters.limit}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, limit: Number(e.target.value), page: 1 }))
                }
                className="filter-select filter-select--sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          {pagination.totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="btn btn-outline btn-sm"
                disabled={filters.page <= 1}
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </button>
              <div className="page-buttons">
                {buildPageList(filters.page, pagination.totalPages).map((item, idx) =>
                  item === "..." ? (
                    <span key={`ellipsis-${idx}`} className="page-ellipsis">…</span>
                  ) : (
                    <button
                      key={item}
                      className={`btn btn-page${filters.page === item ? " active" : ""}`}
                      onClick={() => setFilters((prev) => ({ ...prev, page: item }))}
                    >
                      {item}
                    </button>
                  )
                )}
              </div>
              <button
                className="btn btn-outline btn-sm"
                disabled={filters.page >= pagination.totalPages}
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingAdmin ? 'Edit Admin' : 'Add New Admin'}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              {formError && <div className="error-message">{formError}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="admin@example.com"
                    required
                    disabled={!!editingAdmin}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{editingAdmin ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                  <PasswordInput
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Minimum 8 characters"
                    required={!editingAdmin}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 234 567 8900"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select name="role" value={formData.role} onChange={handleInputChange} required>
                  <option value="super_admin">Super Admin (Full Access)</option>
                  <option value="support">Support (Limited Access)</option>
                  <option value="billing">Billing (Billing Only)</option>
                </select>
              </div>

              {formData.role !== 'super_admin' && (
                <div className="form-section">
                  <h4>Permissions</h4>
                  <div className="features-grid">
                    {availablePermissions.map(perm => (
                      <label key={perm.id} className="feature-checkbox">
                        <input
                          type="checkbox"
                          name="permissions"
                          value={perm.id}
                          checked={formData.permissions.includes(perm.id)}
                          onChange={handleInputChange}
                        />
                        {perm.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancel</button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingAdmin ? 'Update Admin' : 'Create Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .admins-page {
          padding: 0;
        }
        .admin-name .name {
          font-weight: 500;
        }
        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 0.5rem;
        }
        .feature-checkbox {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-size: 0.875rem;
        }
        .feature-checkbox input {
          width: 1rem;
          height: 1rem;
        }
      `}</style>
    </div>
  );
};

export default AdminsList;